import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Stack,
  Popover,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Pagination,
  Skeleton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  SchoolOutlined as SchoolIcon,
  CalendarMonthOutlined as CalendarIcon,
  CardMembershipOutlined as MembershipIcon,
  EventAvailableOutlined as BookingIcon,
  VisibilityOutlined as VisibilityIcon,
  FilterList as FilterListIcon,
  RestartAlt as ResetIcon,
} from "@mui/icons-material";
import {
  useMyInstituteAssignments,
  useMyInstituteFilterOptions,
} from "../../../hooks/useTrainers";
import InstituteBookingModal from "./InstituteBookingModal";
import InstituteEventDetailsModal from "./InstituteEventDetailsModal";

// Helper to format ISO date string
const formatDate = (isoString) => {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return String(isoString);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return String(isoString);
  }
};

// Check if a date is expired
const isDateExpired = (dateStr) => {
  if (!dateStr) return false;
  try {
    const d = new Date(dateStr);
    return !isNaN(d.getTime()) && d < new Date();
  } catch {
    return false;
  }
};

export default function AssignedInstitutesTable({ onNavigateToEDP }) {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedInstituteForDetails, setSelectedInstituteForDetails] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Filter States
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedExpiryDate, setSelectedExpiryDate] = useState("");

  // Limit Filter States
  const [fdpTotal, setFdpTotal] = useState("");
  const [fdpUsed, setFdpUsed] = useState("");
  const [fdpAvailable, setFdpAvailable] = useState("");
  const [techCampTotal, setTechCampTotal] = useState("");
  const [techCampUsed, setTechCampUsed] = useState("");
  const [techCampAvailable, setTechCampAvailable] = useState("");

  // Limit popover anchor
  const [limitAnchorEl, setLimitAnchorEl] = useState(null);

  // Fetch filter options (states and cities dynamically filtered by selectedState)
  const { data: filterOptionsData } = useMyInstituteFilterOptions({
    state_id: selectedState || undefined,
  });

  const statesList = filterOptionsData?.states || [];
  const citiesList = filterOptionsData?.cities || [];

  // Active limit filter count
  const activeLimitCount = useMemo(() => {
    let count = 0;
    if (fdpTotal !== "") count++;
    if (fdpUsed !== "") count++;
    if (fdpAvailable !== "") count++;
    if (techCampTotal !== "") count++;
    if (techCampUsed !== "") count++;
    if (techCampAvailable !== "") count++;
    return count;
  }, [fdpTotal, fdpUsed, fdpAvailable, techCampTotal, techCampUsed, techCampAvailable]);

  // Total active filter count
  const activeFiltersCount = useMemo(() => {
    let count = activeLimitCount;
    if (selectedState !== "") count++;
    if (selectedCity !== "") count++;
    if (selectedExpiryDate !== "") count++;
    return count;
  }, [activeLimitCount, selectedState, selectedCity, selectedExpiryDate]);

  // Reset all filters
  const handleResetFilters = () => {
    setSelectedState("");
    setSelectedCity("");
    setSelectedExpiryDate("");
    setFdpTotal("");
    setFdpUsed("");
    setFdpAvailable("");
    setTechCampTotal("");
    setTechCampUsed("");
    setTechCampAvailable("");
    setPage(1);
  };

  // Reset limit filters only
  const handleClearLimits = () => {
    setFdpTotal("");
    setFdpUsed("");
    setFdpAvailable("");
    setTechCampTotal("");
    setTechCampUsed("");
    setTechCampAvailable("");
    setPage(1);
  };

  const handleOpenDetails = (inst) => {
    setSelectedInstituteForDetails(inst);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsModalOpen(false);
    setSelectedInstituteForDetails(null);
  };
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Debounce search query (400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Query hook with all filters passed to backend
  const { data, isLoading, refetch } = useMyInstituteAssignments({
    search: debouncedSearch,
    page,
    limit,
    state_id: selectedState !== "" ? selectedState : undefined,
    city_name: selectedCity !== "" ? selectedCity : undefined,
    expire_date: selectedExpiryDate !== "" ? selectedExpiryDate : undefined,
    fdp_total: fdpTotal !== "" ? Number(fdpTotal) : undefined,
    fdp_used: fdpUsed !== "" ? Number(fdpUsed) : undefined,
    fdp_available: fdpAvailable !== "" ? Number(fdpAvailable) : undefined,
    tech_camp_total: techCampTotal !== "" ? Number(techCampTotal) : undefined,
    tech_camp_used: techCampUsed !== "" ? Number(techCampUsed) : undefined,
    tech_camp_available: techCampAvailable !== "" ? Number(techCampAvailable) : undefined,
  });

  const institutes = data?.data || [];
  const totalRecords = data?.total || 0;
  const totalPages = data?.total_pages || Math.ceil(totalRecords / limit) || 1;

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setDebouncedSearch("");
    handleResetFilters();
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleOpenBooking = () => {
    setIsBookingModalOpen(true);
  };

  const handleCloseBooking = () => {
    setIsBookingModalOpen(false);
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
      {/* Header section with Title & Top-Right Controls */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "#EEF2FF",
                color: "#4F46E5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SchoolIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  color: "#1E293B",
                  fontSize: { xs: "18px", sm: "20px" },
                  lineHeight: 1.2,
                }}
              >
                Assigned Institutes
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748B",
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "13px",
                  mt: 0.2,
                }}
              >
                Institutes mapped to your trainer profile
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Top-Right Controls: Search Box + Single Booking Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", md: "auto" },
          }}
        >
          {/* Search Box */}
          <TextField
            placeholder="Search institute name..."
            size="small"
            value={searchInput}
            onChange={handleSearchChange}
            sx={{
              width: { xs: "100%", sm: 260 },
              flex: { xs: "1 1 100%", sm: "none" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13.5px",
                bgcolor: "white",
                "&:hover fieldset": { borderColor: "#6366F1" },
                "&.Mui-focused fieldset": { borderColor: "#4F46E5" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ p: 0.5, color: "#94A3B8", "&:hover": { color: "#64748B" } }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Single Top-Right Booking Button */}
          <Button
            variant="contained"
            startIcon={<BookingIcon sx={{ fontSize: 18 }} />}
            onClick={handleOpenBooking}
            sx={{
              bgcolor: "#4F46E5",
              color: "white",
              textTransform: "none",
              fontSize: "13.5px",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2.5,
              py: 0.85,
              boxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
              "&:hover": {
                bgcolor: "#4338CA",
                boxShadow: "0 4px 6px rgba(79, 70, 229, 0.3)",
              },
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Book Slot
          </Button>
        </Box>
      </Box>

      {/* Filters Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2.5,
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          bgcolor: "#FFFFFF",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.03)",
        }}
      >
        {/* State Filter */}
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 170 },
            flex: { xs: "1 1 100%", sm: "none" },
          }}
        >
          <Select
            value={selectedState}
            onChange={(e) => {
              setSelectedState(e.target.value);
              setSelectedCity("");
              setPage(1);
            }}
            displayEmpty
            sx={{
              height: 38,
              fontSize: "13px",
              borderRadius: "8px",
              fontFamily: "'Poppins', sans-serif",
              bgcolor: "#FFFFFF",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: selectedState !== "" ? "#6366F1" : "#E2E8F0",
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
              <em>All States</em>
            </MenuItem>
            {statesList.map((s) => (
              <MenuItem
                key={s.state_id}
                value={s.state_id}
                sx={{ fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}
              >
                {s.state_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* City Filter */}
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 170 },
            flex: { xs: "1 1 100%", sm: "none" },
          }}
        >
          <Select
            value={selectedCity}
            onChange={(e) => {
              setSelectedCity(e.target.value);
              setPage(1);
            }}
            displayEmpty
            sx={{
              height: 38,
              fontSize: "13px",
              borderRadius: "8px",
              fontFamily: "'Poppins', sans-serif",
              bgcolor: "#FFFFFF",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: selectedCity !== "" ? "#6366F1" : "#E2E8F0",
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
              <em>All Cities</em>
            </MenuItem>
            {citiesList.map((c) => (
              <MenuItem
                key={c}
                value={c}
                sx={{ fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}
              >
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Expiry Date Filter */}
        <TextField
          type="date"
          size="small"
          value={selectedExpiryDate}
          onChange={(e) => {
            setSelectedExpiryDate(e.target.value);
            setPage(1);
          }}
          helperText=""
          sx={{
            width: { xs: "100%", sm: 170 },
            flex: { xs: "1 1 100%", sm: "none" },
            "& .MuiOutlinedInput-root": {
              height: 38,
              borderRadius: "8px",
              fontSize: "13px",
              fontFamily: "'Poppins', sans-serif",
              bgcolor: "#FFFFFF",
              "& fieldset": {
                borderColor: selectedExpiryDate ? "#6366F1" : "#E2E8F0",
              },
            },
          }}
        />

        {/* Limit Filters Button */}
        <Button
          variant={activeLimitCount > 0 ? "contained" : "outlined"}
          size="small"
          startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => setLimitAnchorEl(e.currentTarget)}
          sx={{
            height: 38,
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            borderRadius: "8px",
            fontFamily: "'Poppins', sans-serif",
            borderColor: activeLimitCount > 0 ? "#4F46E5" : "#CBD5E1",
            bgcolor: activeLimitCount > 0 ? "#4F46E5" : "transparent",
            color: activeLimitCount > 0 ? "#FFFFFF" : "#475569",
            "&:hover": {
              borderColor: "#4F46E5",
              bgcolor: activeLimitCount > 0 ? "#4338CA" : "#F8FAFC",
            },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          Limit Filters {activeLimitCount > 0 ? "(" + activeLimitCount + ")" : ""}
        </Button>

        {/* Clear All Filters */}
        {activeFiltersCount > 0 && (
          <Button
            size="small"
            startIcon={<ResetIcon sx={{ fontSize: 16 }} />}
            onClick={handleResetFilters}
            sx={{
              height: 38,
              textTransform: "none",
              fontSize: "12.5px",
              color: "#EF4444",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              ml: { xs: 0, sm: "auto" },
              width: { xs: "100%", sm: "auto" },
              "&:hover": { bgcolor: "#FEF2F2" },
            }}
          >
            Clear Filters ({activeFiltersCount})
          </Button>
        )}
      </Paper>

      {/* Limit Filters Popover */}
      <Popover
        open={Boolean(limitAnchorEl)}
        anchorEl={limitAnchorEl}
        onClose={() => setLimitAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 2.5,
            width: { xs: "90vw", sm: 360 },
            borderRadius: "14px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "14px",
              color: "#1E293B",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Filter by Limits
          </Typography>
          {activeLimitCount > 0 && (
            <Button
              size="small"
              onClick={handleClearLimits}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                color: "#EF4444",
                p: 0,
                minWidth: 0,
                fontFamily: "'Poppins', sans-serif",
                "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
              }}
            >
              Reset
            </Button>
          )}
        </Box>

        {/* FDP Section */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: "11px",
            fontWeight: 700,
            color: "#2563EB",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontFamily: "'Poppins', sans-serif",
            mb: 1,
          }}
        >
          FDP / Educator Limits
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 2 }}>
          <TextField
            label="Total"
            size="small"
            type="number"
            value={fdpTotal}
            onChange={(e) => {
              setFdpTotal(e.target.value);
              setPage(1);
            }}
            inputProps={{ min: 0 }}
            sx={{ "& .MuiInputBase-input": { fontSize: "13px", py: 1 } }}
          />
          <TextField
            label="Used"
            size="small"
            type="number"
            value={fdpUsed}
            onChange={(e) => {
              setFdpUsed(e.target.value);
              setPage(1);
            }}
            inputProps={{ min: 0 }}
            sx={{ "& .MuiInputBase-input": { fontSize: "13px", py: 1 } }}
          />
          <TextField
            label="Available"
            size="small"
            type="number"
            value={fdpAvailable}
            onChange={(e) => {
              setFdpAvailable(e.target.value);
              setPage(1);
            }}
            inputProps={{ min: 0 }}
            sx={{ "& .MuiInputBase-input": { fontSize: "13px", py: 1 } }}
          />
        </Box>

        {/* Tech Camp Section */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: "11px",
            fontWeight: 700,
            color: "#D97706",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontFamily: "'Poppins', sans-serif",
            mb: 1,
          }}
        >
          Tech Camp Limits
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 2.5 }}>
          <TextField
            label="Total"
            size="small"
            type="number"
            value={techCampTotal}
            onChange={(e) => {
              setTechCampTotal(e.target.value);
              setPage(1);
            }}
            inputProps={{ min: 0 }}
            sx={{ "& .MuiInputBase-input": { fontSize: "13px", py: 1 } }}
          />
          <TextField
            label="Used"
            size="small"
            type="number"
            value={techCampUsed}
            onChange={(e) => {
              setTechCampUsed(e.target.value);
              setPage(1);
            }}
            inputProps={{ min: 0 }}
            sx={{ "& .MuiInputBase-input": { fontSize: "13px", py: 1 } }}
          />
          <TextField
            label="Available"
            size="small"
            type="number"
            value={techCampAvailable}
            onChange={(e) => {
              setTechCampAvailable(e.target.value);
              setPage(1);
            }}
            inputProps={{ min: 0 }}
            sx={{ "& .MuiInputBase-input": { fontSize: "13px", py: 1 } }}
          />
        </Box>

        <Button
          fullWidth
          variant="contained"
          size="small"
          onClick={() => setLimitAnchorEl(null)}
          sx={{
            bgcolor: "#4F46E5",
            color: "white",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13px",
            borderRadius: "8px",
            py: 0.8,
            boxShadow: "none",
            fontFamily: "'Poppins', sans-serif",
            "&:hover": { bgcolor: "#4338CA" },
          }}
        >
          Done
        </Button>
      </Popover>

      {/* Mobile & Tablet Card View (visible on screens < 900px) */}
      <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
        {isLoading ? (
          <Stack spacing={2}>
            {Array.from(new Array(3)).map((_, idx) => (
              <Paper
                key={`skeleton-card-${idx}`}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: "1px solid #E2E8F0",
                  bgcolor: "white",
                }}
              >
                <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
                  <Skeleton variant="rounded" width={28} height={28} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="75%" height={22} />
                    <Skeleton width="40%" height={16} sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
                <Skeleton width="100%" height={1} sx={{ my: 1.5 }} />
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                  <Box>
                    <Skeleton width="50%" height={14} />
                    <Skeleton width="70%" height={20} sx={{ mt: 0.5 }} />
                    <Skeleton width="85%" height={16} sx={{ mt: 0.5 }} />
                  </Box>
                  <Box>
                    <Skeleton width="50%" height={14} />
                    <Skeleton width="60%" height={22} sx={{ mt: 0.5 }} />
                    <Skeleton width="80%" height={16} sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
                <Skeleton variant="rounded" width="100%" height={36} sx={{ mt: 2 }} />
              </Paper>
            ))}
          </Stack>
        ) : institutes.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              bgcolor: "white",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: "#F1F5F9",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#94A3B8",
                mb: 1.5,
              }}
            >
              <SchoolIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#334155", fontFamily: "'Poppins', sans-serif" }}
            >
              {debouncedSearch ? "No matching institutes found" : "No Assigned Institutes"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#94A3B8", fontFamily: "'Poppins', sans-serif", fontSize: "13px", mt: 0.5 }}
            >
              {debouncedSearch
                ? `No institute matching "${debouncedSearch}". Try another keyword.`
                : "You do not have any institutes assigned to your profile yet."}
            </Typography>
            {debouncedSearch && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearSearch}
                sx={{
                  textTransform: "none",
                  fontSize: "12.5px",
                  fontFamily: "'Poppins', sans-serif",
                  mt: 1.5,
                  borderColor: "#CBD5E1",
                  color: "#475569",
                }}
              >
                Clear Search
              </Button>
            )}
          </Paper>
        ) : (
          <Stack spacing={2}>
            {institutes.map((inst, index) => {
              const sNo = (page - 1) * limit + index + 1;
              const expired = isDateExpired(inst.expire_date);

              return (
                <Paper
                  key={inst.institute_id || index}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    border: "1px solid #E2E8F0",
                    bgcolor: "#FFFFFF",
                    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {/* Card Header: S.No, Name, Type, Location */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box
                      sx={{
                        minWidth: 26,
                        height: 26,
                        borderRadius: "6px",
                        bgcolor: "#F1F5F9",
                        color: "#475569",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: 600,
                        fontFamily: "'Poppins', sans-serif",
                        mt: 0.2,
                      }}
                    >
                      {sNo}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#1E293B",
                          fontSize: "14px",
                          fontFamily: "'Poppins', sans-serif",
                          lineHeight: 1.35,
                        }}
                      >
                        {inst.institute_name || "—"}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                        {inst.institute_type && (
                          <Chip
                            label={inst.institute_type}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "11px",
                              fontWeight: 500,
                              bgcolor: "#F1F5F9",
                              color: "#475569",
                              borderRadius: "4px",
                              fontFamily: "'Poppins', sans-serif",
                            }}
                          />
                        )}
                        {(inst.city_name || inst.state_name) && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748B",
                              fontSize: "12px",
                              fontFamily: "'Poppins', sans-serif",
                            }}
                          >
                            {[inst.city_name, inst.state_name].filter(Boolean).join(", ")}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: "#F1F5F9" }} />

                  {/* SPOC Details & Plan/Expiry Grid */}
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    {/* SPOC Column */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#94A3B8",
                          fontSize: "10.5px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          fontFamily: "'Poppins', sans-serif",
                          display: "block",
                          mb: 0.3,
                        }}
                      >
                        SPOC Details
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#334155",
                          fontSize: "13px",
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        {inst.educator_name || inst.spoc_name || "—"}
                      </Typography>
                      {inst.educator_email && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748B",
                            fontSize: "12px",
                            fontFamily: "'Poppins', sans-serif",
                            display: "block",
                            wordBreak: "break-all",
                          }}
                        >
                          {inst.educator_email}
                        </Typography>
                      )}
                      {inst.educator_phone && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748B",
                            fontSize: "12px",
                            fontFamily: "'Poppins', sans-serif",
                            display: "block",
                          }}
                        >
                          {inst.educator_phone}
                        </Typography>
                      )}
                    </Box>

                    {/* Plan & Expiry Column */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#94A3B8",
                          fontSize: "10.5px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          fontFamily: "'Poppins', sans-serif",
                          display: "block",
                          mb: 0.3,
                        }}
                      >
                        Plan & Expiry
                      </Typography>
                      <Box sx={{ mb: 0.5 }}>
                        {inst.membership_name ? (
                          <Chip
                            icon={<MembershipIcon style={{ fontSize: 13, color: "#4338CA" }} />}
                            label={inst.membership_name}
                            size="small"
                            sx={{
                              bgcolor: "#EEF2FF",
                              color: "#4338CA",
                              fontWeight: 600,
                              fontSize: "11px",
                              fontFamily: "'Poppins', sans-serif",
                              border: "1px solid #E0E7FF",
                              borderRadius: "5px",
                              height: 22,
                            }}
                          />
                        ) : (
                          <Typography sx={{ color: "#94A3B8", fontSize: "12px", fontFamily: "'Poppins', sans-serif" }}>
                            —
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 14, color: expired ? "#EF4444" : "#64748B" }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "12px",
                            fontWeight: 500,
                            color: expired ? "#DC2626" : "#334155",
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          {formatDate(inst.expire_date)}
                        </Typography>
                        {expired && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#EF4444", fontSize: "10.5px", fontWeight: 700 }}
                          >
                            (Expired)
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Action View Button */}
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                    onClick={() => handleOpenDetails(inst)}
                    sx={{
                      textTransform: "none",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      fontFamily: "'Poppins', sans-serif",
                      borderColor: "#CBD5E1",
                      color: "#2563EB",
                      borderRadius: "8px",
                      py: 0.8,
                      mt: 0.5,
                      "&:hover": {
                        borderColor: "#2563EB",
                        bgcolor: "#EFF6FF",
                      },
                    }}
                  >
                    View Limits & Events
                  </Button>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Desktop Table View (visible on screens >= 900px, with horizontal scroll) */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          display: { xs: "none", md: "block" },
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          overflowX: "auto",
          bgcolor: "white",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Table sx={{ minWidth: 900 }} aria-label="assigned institutes table">
          <TableHead sx={{ bgcolor: "#F8FAFC" }}>
            <TableRow sx={{ borderBottom: "1px solid #E2E8F0" }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  width: 50,
                }}
              >
                #
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  minWidth: 200,
                }}
              >
                Institute
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  minWidth: 160,
                }}
              >
                Spoc Details
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  width: 150,
                }}
              >
                Membership Plan
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  width: 130,
                }}
              >
                Expire Date
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  width: 110,
                }}
              >
                View
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              // Loading Skeletons
              Array.from(new Array(5)).map((_, idx) => (
                <TableRow key={`skeleton-${idx}`}>
                  <TableCell><Skeleton width={20} /></TableCell>
                  <TableCell>
                    <Skeleton width="60%" height={24} />
                    <Skeleton width="40%" height={16} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width="60%" height={20} />
                    <Skeleton width="80%" height={16} />
                  </TableCell>
                  <TableCell><Skeleton width={100} height={28} /></TableCell>
                  <TableCell><Skeleton width={90} /></TableCell>
                  <TableCell align="center"><Skeleton width={60} height={28} sx={{ mx: "auto" }} /></TableCell>
                </TableRow>
              ))
            ) : institutes.length === 0 ? (
              // Empty State
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        bgcolor: "#F1F5F9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#94A3B8",
                      }}
                    >
                      <SchoolIcon sx={{ fontSize: 30 }} />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      {debouncedSearch ? "No matching institutes found" : "No Assigned Institutes"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#94A3B8",
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: "13px",
                        maxWidth: 360,
                        textAlign: "center",
                      }}
                    >
                      {debouncedSearch
                        ? `No institute matching "${debouncedSearch}". Try another search keyword.`
                        : "You do not have any institutes assigned to your profile yet."}
                    </Typography>
                    {debouncedSearch && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleClearSearch}
                        sx={{
                          textTransform: "none",
                          fontSize: "12.5px",
                          fontFamily: "'Poppins', sans-serif",
                          mt: 1,
                          borderColor: "#CBD5E1",
                          color: "#475569",
                        }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              institutes.map((inst, index) => {
                const sNo = (page - 1) * limit + index + 1;
                const expired = isDateExpired(inst.expire_date);

                return (
                  <TableRow
                    key={inst.institute_id || index}
                    sx={{
                      "&:hover": { bgcolor: "#F8FAFC" },
                      transition: "background-color 0.15s ease",
                      borderBottom: "1px solid #F1F5F9",
                    }}
                  >
                    {/* S.No */}
                    <TableCell
                      sx={{
                        color: "#64748B",
                        fontSize: "13px",
                        fontWeight: 500,
                        fontFamily: "'Poppins', sans-serif",
                        py: 2,
                      }}
                    >
                      {sNo}
                    </TableCell>

                    {/* Institute Name, Type & Location */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "#1E293B",
                            fontSize: "14px",
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          {inst.institute_name || "—"}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          {inst.institute_type && (
                            <Chip
                              label={inst.institute_type}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "11px",
                                fontWeight: 500,
                                bgcolor: "#F1F5F9",
                                color: "#475569",
                                borderRadius: "4px",
                                fontFamily: "'Poppins', sans-serif",
                              }}
                            />
                          )}
                          {(inst.city_name || inst.state_name) && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#64748B",
                                fontSize: "12px",
                                fontFamily: "'Poppins', sans-serif",
                              }}
                            >
                              {[inst.city_name, inst.state_name].filter(Boolean).join(", ")}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Spoc Details (Name, Email, Phone merged) */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "#1E293B",
                            fontSize: "13.5px",
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          {inst.educator_name || inst.spoc_name || "—"}
                        </Typography>
                        {inst.educator_email && (
                          <Typography
                            variant="caption"
                            title={inst.educator_email}
                            sx={{
                              color: "#64748B",
                              fontSize: "12px",
                              fontFamily: "'Poppins', sans-serif",
                              wordBreak: "break-all",
                            }}
                          >
                            {inst.educator_email}
                          </Typography>
                        )}
                        {inst.educator_phone && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748B",
                              fontSize: "12px",
                              fontFamily: "'Poppins', sans-serif",
                            }}
                          >
                            {inst.educator_phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Membership Plan */}
                    <TableCell sx={{ py: 2 }}>
                      {inst.membership_name ? (
                        <Chip
                          icon={<MembershipIcon style={{ fontSize: 14, color: "#4338CA" }} />}
                          label={inst.membership_name}
                          size="small"
                          sx={{
                            bgcolor: "#EEF2FF",
                            color: "#4338CA",
                            fontWeight: 600,
                            fontSize: "12px",
                            fontFamily: "'Poppins', sans-serif",
                            border: "1px solid #E0E7FF",
                            borderRadius: "6px",
                            px: 0.5,
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: "#94A3B8", fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* Expire Date */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: expired ? "#EF4444" : "#64748B" }} />
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: expired ? "#DC2626" : "#334155",
                              fontFamily: "'Poppins', sans-serif",
                            }}
                          >
                            {formatDate(inst.expire_date)}
                          </Typography>
                          {expired && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#EF4444", fontSize: "10.5px", fontWeight: 600 }}
                            >
                              Expired
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* View Action */}
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                        onClick={() => handleOpenDetails(inst)}
                        sx={{
                          textTransform: "none",
                          fontSize: "12.5px",
                          fontWeight: 600,
                          fontFamily: "'Poppins', sans-serif",
                          borderColor: "#CBD5E1",
                          color: "#2563EB",
                          borderRadius: "6px",
                          py: 0.4,
                          px: 1.5,
                          "&:hover": {
                            borderColor: "#2563EB",
                            bgcolor: "#EFF6FF",
                          },
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination & Footer Controls */}
      {!isLoading && institutes.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mt: 2.5,
            px: 1,
          }}
        >
          {/* Rows per page selector */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography
              variant="body2"
              sx={{ color: "#64748B", fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}
            >
              Rows per page:
            </Typography>
            <FormControl size="small">
              <Select
                value={limit}
                onChange={handleLimitChange}
                sx={{
                  height: 32,
                  fontSize: "13px",
                  borderRadius: "6px",
                  bgcolor: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                  },
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Typography
              variant="body2"
              sx={{ color: "#94A3B8", fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}
            >
              Showing {Math.min((page - 1) * limit + 1, totalRecords)} -{" "}
              {Math.min(page * limit, totalRecords)} of {totalRecords}
            </Typography>
          </Box>

          {/* Pagination */}
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            size={isMobile ? "small" : "medium"}
            siblingCount={isMobile ? 0 : 1}
            color="primary"
            shape="rounded"

            sx={{
              "& .MuiPaginationItem-root": {
                fontFamily: "'Poppins', sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                borderRadius: "6px",
                "&.Mui-selected": {
                  bgcolor: "#4F46E5",
                  color: "white",
                  "&:hover": { bgcolor: "#4338CA" },
                },
              },
            }}
          />
        </Box>
      )}

      {/* Booking Action Modal */}
      <InstituteBookingModal
        open={isBookingModalOpen}
        onClose={handleCloseBooking}
        institutes={institutes}
        onSuccess={(msg) => {
          showSnackbar(msg || "Slot booked successfully!", "success");
          refetch();
        }}
      />

      {/* Institute Events & Limits History Modal */}
      <InstituteEventDetailsModal
        open={isDetailsModalOpen}
        onClose={handleCloseDetails}
        institute={selectedInstituteForDetails}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
